import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import isEmpty from 'lodash-es/isEmpty';
import { Observable } from 'rxjs';
import { map, share } from 'rxjs/operators';

import { AppFacade } from '@app/app.facade';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { BusResultsFacade } from '@buses/bus-results/bus-results.facade';
import { AirSearchPayload } from '@flight-results/entities/airSearchPayload';
import { AirportsFilter } from '@flight-results/entities/request-search.payload';
import { FlightResultsFacade } from '@flight-results/flight-results.facade';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { FlightsModalFiltersAbstractComponent } from '@shared/filters/abstract/flights-filters.abstract';
import { FilterApplier, FilterTypes } from '@shared/flight-itinerary/entities/filters';
import { SorterTypes } from '@shared/filters/entities/sorter-options';
import { WidgetsFacade } from '@shared/widgets/store/widgets.facade';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { environment as ENV } from '@environment';
import { MODAL_HEIGHT } from '@app/constants/modal-heights';
import { StatsigExperimentsFacade } from '@statsig/statsig.facade';
import { AirFilterVm } from '@shared/magic-filter-bar/entities/filter-types';
import { ButtonSize } from '@design-system/components/molecules/button/entities/button.types';

@Component({
  selector: 'app-flights-filters',
  templateUrl: './flights-filters.container.html',
  styleUrls: ['./flights-filters.container.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FlightsFiltersContainer extends FlightsModalFiltersAbstractComponent implements OnInit, OnDestroy {

  public alertImageNotification: string = 'ilustra-alertas.png';
  public currencyCode: string = ENV.countryInfo.currency_code;
  public showAlertsManagement: boolean = false;
  public isCreateAlertMode: boolean = false;
  public isDeleteAlertMode: boolean = false;
  public notifyAlertCreated: boolean = false;
  public modalHeight: string = MODAL_HEIGHT.FULL;
  public sortFligth: string = SorterTypes.Recommended;

  public readonly alertButtonSize = ButtonSize.SM;

  constructor(
    private router: Router,
    private facade: WidgetsFacade,
    private changeDetector: ChangeDetectorRef,
    protected appFacade: AppFacade,
    protected busFacade: BusResultsFacade,
    protected fligthfacade: FlightResultsFacade,
    protected baseSearchBarFacade: BaseSearchBarFacade,
    protected translateService: TranslocoService,
    protected amplitude: PropertiesAmplitudeService,
    protected triggerAmplitudeService: TriggerAmplitudeService,
    protected route: ActivatedRoute,
    protected domManipulateService: DomManipulateService,
    protected statsigExperimentsFacade: StatsigExperimentsFacade,
    @Inject(IS_BROWSER) protected isBrowser: boolean) {
    super(
      appFacade,
      busFacade,
      fligthfacade,
      baseSearchBarFacade,
      route, amplitude,
      triggerAmplitudeService,
      translateService,
      domManipulateService,
      statsigExperimentsFacade,
      isBrowser
    );
    const currentNav = this.router.getCurrentNavigation();
    if (!currentNav || !currentNav?.extras) return;
    const routerState = currentNav?.extras?.state;
    if (!routerState?.isMobile) return;
    this.isBuses = (routerState && routerState?.isBuses ? routerState?.isBuses : false);
    this.isChanges = (routerState && routerState?.isChanges ? routerState?.isChanges : false);
    this.isBySector = (routerState && routerState?.isBySector ? routerState?.isBySector : false);
    this.isMobile = (routerState && routerState?.isMobile ? routerState?.isMobile : false);
  }

  ngOnInit(): void {
    this.isBuses = this.filterBuses || this.isBuses;
    this.isFilterPrice = true;
    this.listenParams();
    this.defineBodyScroll(false);
  }

  ngOnDestroy(): void {
    this.defineBodyScroll(true);
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  get isLoggedIn$(): Observable<boolean> {
    return this.appFacade.isLoggedIn$;
  }

  get showBtnAlerts$(): Observable<boolean> {
    return this.isLoggedIn$.pipe(map(isLoggedIn => !this.filterBuses && isLoggedIn && !this.isMobile));
  }

  get activateAlerts$(): Observable<boolean> {
    return this.facade.activeAlerts$;
  }

  get alertSelected(): void {
    return this.facade.alert$.subscribe(alert => alert).unsubscribe();
  }

  get priceAlertSelected$(): Observable<number> {
    return this.facade.alert$.pipe(
      map(alert => +alert.price_max), share());
  }

  get showRangeFilters(): boolean {
    return this.isMobile && !this.isBuses;
  }

  public sorting(filter: FilterApplier): void {
    if (!this.isBuses) {
      this.fligthfacade.flightSortingHasBeenChanged(true);
    }
    this.searchFlightsWithFilter(filter);
  }

  public clearFilterAndResults(): void {
    if ((!isEmpty(this.params) && this.totalFilterCount > 0) || this.hasChangeSortByFilter) {
      this.pickedCheckboxes = [];
      this.pickedAirportsCheckboxes = { DepartureAirports: [], ArrivalAirports: [] } as AirportsFilter;
      this.pickedRanges = {};
      this.hasChangeSortByFilter = false;
      this.changeDetector.detectChanges();
      this.domManipulateService.resetScrollPosition();
      if (this.isBuses) {
        this.busFacade.searchBuses(this.params as AirSearchPayload);
      } else {
        this.fligthfacade.flightSortingHasBeenChanged(false);
        this.fligthfacade.clearFilterAndResults(this.isBySector);
      }
    }
  }

  public orderChecks(item: AirFilterVm, index: number): number {
    return item.value && (item.type === FilterTypes.Tags || item.type === FilterTypes.AirFilterTabs) ? -1 : index;
  }

  public toggleShowCreateAlertForm(): void {
    this.showAlertsManagement = !this.showAlertsManagement;
    this.isCreateAlertMode = true;
    this.isDeleteAlertMode = false;
    this.domManipulateService.setBodyScroll(!this.showAlertsManagement);
  }

  public toggleConfirmDeleteAlert(): void {
    this.showAlertsManagement = !this.showAlertsManagement;
    this.isCreateAlertMode = false;
    this.isDeleteAlertMode = true;
    this.domManipulateService.setBodyScroll(!this.showAlertsManagement);
  }

  public toggleDesktopAlertsManagement(): void {
    this.showAlertsManagement = !this.showAlertsManagement;
    this.isCreateAlertMode = false;
    this.isDeleteAlertMode = false;
    this.domManipulateService.setBodyScroll(!this.showAlertsManagement);
  }

  public toggleAlertCreated(): void {
    this.notifyAlertCreated = !this.notifyAlertCreated;
  }
}
